import { STELLING } from '../indeling'
import { duurVan, maakProfiel, opProfiel, volg } from './beweging'
import type { Profiel, Stap } from './beweging'

/**
 * De werkcyclus van de K- en R-MATIC: pallets uit de stelling halen, meenemen
 * en elders wegzetten. Alles hier is een functie van de tijd, zonder toestand,
 * zodat de scène op elk moment kan beginnen en de pallets toch kloppen.
 *
 * ## Dubbel spel
 *
 * Een truck heeft twee werkpunten in de stelling, A en B, elk met twee vakken
 * boven elkaar: het eerste en het tweede niveau van hetzelfde vak. Bij elke
 * stop zet hij de pallet die hij bij zich heeft weg in het lege vak, en haalt
 * hij uit het andere vak de volgende. Die rijdt hij naar het andere werkpunt.
 * Zo rijdt hij nooit leeg, en blijft het aantal pallets gelijk: drie pallets
 * over vier vakken en de vorken.
 *
 * Met één handeling per stop kan dat niet: dan loopt het ene werkpunt leeg en
 * het andere vol. Een magazijnsysteem plant dit ook zo — opslaan en uitslaan
 * in één rit.
 *
 * ## De boekhouding
 *
 * Stop k is bij A als k even is, anders bij B; v = ⌊k/2⌋ is het hoeveelste
 * bezoek aan dat werkpunt. Bij bezoek v is vak (v+1) mod 2 leeg en haalt hij
 * vak v mod 2 leeg. Op de vorken:
 *
 *   vóór stop k:  pallet (k−1) mod 3
 *   na stop k:    pallet k mod 3
 *
 * Daaruit volgt de rest (zie `bezetting`).
 */

/* ------------------------------------------------------------------ stappen */

/** Wat er tijdens een stop beweegt. */
export interface Kanalen {
  /** Draaiing om y; 0 is met de vorken naar de stelling (R-MATIC). */
  yaw: number
  /** Hoe ver hij uit het gangpadmidden naar de stelling is gereden (R-MATIC). */
  naar: number
  /** Midden van het vorkblad boven de vloer. */
  hoogte: number
  /** Vorken de stelling in: 0 ingetrokken, 1 helemaal uit. */
  uit: number
}

type Kanaal = keyof Kanalen

export interface Stand extends Kanalen {
  gezet: boolean
  gepakt: boolean
}

/**
 * Stand op tijd t in een reeks stappen. Elk kanaal gaat per stap volgens het
 * rijprofiel (`beweging.ts`) van waar het was naar zijn doel, en staat dus
 * stil op elk doel. Dat is ook het moment waarop een pallet van de vorken in
 * het vak overgaat of andersom: stil, dus zonder sprong. "gezet" valt vóór
 * "gepakt", dus na het pakken is hij allebei.
 */
function standIn(stappen: Stap<Kanaal>[], begin: Kanalen, t: number): Stand {
  const { kanalen, laatste } = volg(stappen, { ...begin }, t)
  return { ...kanalen, gezet: laatste !== undefined, gepakt: laatste === 'gepakt' }
}

/** Een stap duurt minstens zo lang: ook een vork die 6 cm zakt neemt even de tijd. */
const MIN_STAP = 0.4

/* ------------------------------------------------------------------ het vak */

/** Onderkant van een pallet in de stelling boven zijn ligger. */
const PALLET_OP_LIGGER = 0.02
/** Onderkant van de bovenplanken van een europallet: daar drukt de vork tegen. */
const PALLET_OPENING = 0.122

/** Onderkant van de pallet op niveau j (0 = eerste ligger); ook voor de vaste lading (`Lading`). */
export const palletY = (j: number) => (j + 1) * STELLING.niveauHoogte + PALLET_OP_LIGGER

/**
 * Hoogtes van het vorkblad (midden) bij een vak: `vrij` midden in de
 * palletopening en net boven de ligger, `raak` waar het blad de pallet
 * raakt, `boven` 6 cm hoger — de pallet los van de stelling.
 */
export function vak(j: number, blad: number) {
  const y = palletY(j)
  const raak = y + PALLET_OPENING - blad / 2
  return { vrij: y + 0.08, raak, boven: raak + 0.06 }
}

/** Waar de pallet ligt als hij op de vorken ligt: zijn onderkant onder het midden van het blad. */
export const palletOpVork = (blad: number) => blad / 2 - PALLET_OPENING

/* ------------------------------------------------------------------ de machine */

/**
 * Hoe een model werkt: wat de werkcyclus van de truck moet weten. Snelheden
 * van het typeblad in m/s (draaien in rad/s), versnellingen in m/s²; de
 * waarden staan in `tempo.ts`.
 */
export interface Werktuig {
  /** Draait hij een kwartslag de stelling in (reachtruck), of zet hij zijn vorken dwars? */
  draait: boolean
  /** Hoe ver hij na het draaien naar de stelling rijdt. */
  naar: number
  /** Vorkblad bij het rijden: met een pallet erop net vrij van chassis of poten. */
  rij: number
  /** Dikte van het vorkblad. */
  blad: number
  /** Hef- en daalsnelheid, met en zonder last. */
  heffen: { last: number; leeg: number }
  dalen: { last: number; leeg: number }
  hefA: number
  /** De vorken de stelling in: hoe ver (m), hoe snel, en hoe snel op gang. */
  uit: { weg: number; v: number; a: number }
  /** Een kwartslag draaien, en het stukje naar de stelling. */
  draai: { w: number; a: number }
  nader: { v: number; a: number }
  /** Topsnelheid in het gangpad, naar B en naar A, en de versnelling. */
  rijden: { naarB: number; naarA: number; a: number }
}

/**
 * Hoe hij in het gangpad staat: met de vorken naar A (−x), zoals de camera
 * van die hotspot. Naar B rijdt hij achteruit, zodat je de pallet die hij
 * net bij A ophaalde ook ziet wegrijden; bij de R-MATIC draait hij daarvoor
 * bij elke stop een kwartslag heen en terug.
 */
const RIJ_YAW = -Math.PI / 2

/** Zo staat hij tussen twee stops: rijklaar, vorken in, blad op rijhoogte. */
const rust = (w: Werktuig): Kanalen => ({ yaw: RIJ_YAW, naar: 0, hoogte: w.rij, uit: 0 })

/**
 * De stappen van één stop: wegzetten in vak `zet`, ophalen uit vak `haal`.
 * Elke stap krijgt zijn profiel uit de afstand en de snelheid van het
 * typeblad; heffen en dalen gaan met last anders dan zonder, dus houden we
 * bij of er een pallet op de vorken ligt.
 */
function stopStappen(w: Werktuig, zet: number, haal: number): Stap<Kanaal>[] {
  const z = vak(zet, w.blad)
  const h = vak(haal, w.blad)
  const st: Stap<Kanaal>[] = []
  const nu = rust(w)
  // Hij komt aan met de pallet die hij brengt.
  let geladen = true
  const stap = (doel: Partial<Kanalen>, weg: number, v: number, a: number, gebeurt?: 'gezet' | 'gepakt') => {
    st.push({ doel, profiel: maakProfiel(weg, v, a, MIN_STAP), gebeurt })
    Object.assign(nu, doel)
    if (gebeurt) geladen = gebeurt === 'gepakt'
  }
  const hef = (doel: number, gebeurt?: 'gezet' | 'gepakt') => {
    const tempo = doel > nu.hoogte ? w.heffen : w.dalen
    stap({ hoogte: doel }, Math.abs(doel - nu.hoogte), geladen ? tempo.last : tempo.leeg, w.hefA, gebeurt)
  }
  const uit = (doel: number) => stap({ uit: doel }, Math.abs(doel - nu.uit) * w.uit.weg, w.uit.v, w.uit.a)

  if (w.draait) {
    stap({ yaw: 0 }, Math.abs(nu.yaw), w.draai.w, w.draai.a)
    stap({ naar: w.naar }, w.naar, w.nader.v, w.nader.a)
  }
  hef(z.boven)
  uit(1)
  hef(z.raak, 'gezet')
  hef(z.vrij)
  uit(0)
  hef(h.vrij)
  uit(1)
  hef(h.raak, 'gepakt')
  hef(h.boven)
  uit(0)
  hef(w.rij)
  if (w.draait) {
    stap({ naar: 0 }, w.naar, w.nader.v, w.nader.a)
    stap({ yaw: RIJ_YAW }, Math.abs(RIJ_YAW), w.draai.w, w.draai.a)
  }
  return st
}

/* ------------------------------------------------------------------ de cyclus */

export interface Cyclus {
  /** Duur van een stop: de langste van de twee varianten. */
  stop: number
  /** De stappen per variant, op het lege vak (0 of 1) — één keer berekend. */
  stappen: [Stap<Kanaal>[], Stap<Kanaal>[]]
  /** De ritten van A naar B en terug; bij de R-MATIC niet even snel. */
  naarB: Profiel
  naarA: Profiel
  /** Heen en terug: twee stops en twee ritten. */
  paar: number
}

/** Welk vak bij bezoek v leeg is en welk vol. */
const leegVak = (v: number) => (((v + 1) % 2) + 2) % 2

export function maakCyclus(w: Werktuig, afstand: number): Cyclus {
  // Eerst boven wegzetten of eerst onder: de twee varianten duren niet exact
  // even lang, want met last heft en daalt hij anders dan zonder. De stop
  // duurt de langste; bij de kortere wacht hij even voor hij wegrijdt.
  const stappen: Cyclus['stappen'] = [stopStappen(w, 0, 1), stopStappen(w, 1, 0)]
  const stop = Math.max(duurVan(stappen[0]), duurVan(stappen[1]))
  const naarB = maakProfiel(afstand, w.rijden.naarB, w.rijden.a)
  const naarA = maakProfiel(afstand, w.rijden.naarA, w.rijden.a)
  return { stop, stappen, naarB, naarA, paar: 2 * stop + naarB.duur + naarA.duur }
}

export interface Moment {
  /** Het segment: stop k met de halve ritten ervoor en erna. */
  k: number
  /** Waar hij is: 0 bij A, 1 bij B, ertussenin tijdens het rijden. */
  ab: number
  /** Richting waarin hij rijdt (−1 naar A, +1 naar B), 0 als hij stilstaat. */
  rijrichting: -1 | 0 | 1
  /** Snelheid langs A → B in m/s, negatief naar A: de Bluespot hangt eraan. */
  snelheid: number
  /** Staat hij bij een stop (en niet te rijden)? */
  bijStop: boolean
  stand: Stand
}

/**
 * Waar de truck is en wat hij doet op tijd τ (seconden). Een periode is een
 * stop bij A, de rit naar B, een stop bij B en de rit terug. Een segment loopt
 * van halverwege de rit naar stop k tot halverwege de rit erna: op dat moment
 * verandert er niets aan de pallets, en daar mag de telling dus verspringen.
 */
export function moment(w: Werktuig, c: Cyclus, tau: number): Moment {
  const n = Math.floor(tau / c.paar)
  let r = tau - n * c.paar
  const kA = 2 * n
  const kB = kA + 1
  if (r < c.stop) return opStop(w, c, kA, r)
  r -= c.stop
  if (r < c.naarB.duur) return opRit(w, c.naarB, r, kA, kB, 1)
  r -= c.naarB.duur
  if (r < c.stop) return opStop(w, c, kB, r)
  r -= c.stop
  return opRit(w, c.naarA, r, kB, kB + 1, -1)
}

function opStop(w: Werktuig, c: Cyclus, k: number, r: number): Moment {
  const stappen = c.stappen[leegVak(Math.floor(k / 2))]
  return { k, ab: k % 2 === 0 ? 0 : 1, rijrichting: 0, snelheid: 0, bijStop: true, stand: standIn(stappen, rust(w), r) }
}

/** De rit na stop `kVoor` naar stop `kNa`; de telling verspringt halverwege. */
function opRit(w: Werktuig, p: Profiel, r: number, kVoor: number, kNa: number, zin: 1 | -1): Moment {
  const k = r < p.duur / 2 ? kVoor : kNa
  const { s, v } = opProfiel(p, r)
  const deel = p.weg === 0 ? 1 : s / p.weg
  // In de eerste helft is de stop ervoor af; in de tweede telt de volgende al.
  const af = k === kVoor
  return {
    k, ab: zin > 0 ? deel : 1 - deel, rijrichting: zin, snelheid: zin * v, bijStop: false,
    stand: { ...rust(w), gezet: af, gepakt: af },
  }
}

/* ------------------------------------------------------------------ bezetting */

const mod3 = (n: number) => ((n % 3) + 3) % 3

/**
 * Welke pallet (0, 1, 2) waar staat in segment k. `vakken[w][j]` is werkpunt
 * w (0 = A, 1 = B), niveau j: het nummer van de pallet die er in dit segment
 * staat of komt te staan, en wanneer hij er zichtbaar is.
 */
export interface Bezetting {
  vakken: { pallet: number; zichtbaar: (s: Stand) => boolean }[][]
  /** Op de vorken: de pallet die hij brengt, en die hij meeneemt. */
  brengt: number
  neemt: number
}

export function bezetting(k: number): Bezetting {
  const hier = k % 2 === 0 ? 0 : 1
  const v = Math.floor(k / 2)
  // Het andere werkpunt, zoals de vorige stop (k − 1) het achterliet.
  const vOud = Math.floor((k - 1) / 2)
  const nooit = () => false
  const altijd = () => true
  const vakken: Bezetting['vakken'] = [[], []]
  for (const w of [0, 1]) {
    for (const j of [0, 1]) {
      if (w === hier) {
        vakken[w][j] = j === leegVak(v)
          ? { pallet: mod3(k - 1), zichtbaar: (s) => s.gezet }
          : { pallet: mod3(k), zichtbaar: (s) => !s.gepakt }
      } else {
        vakken[w][j] = j === leegVak(vOud)
          ? { pallet: mod3(k - 2), zichtbaar: altijd }
          : { pallet: 0, zichtbaar: nooit }
      }
    }
  }
  return { vakken, brengt: mod3(k - 1), neemt: mod3(k) }
}
